import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Spinner,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { compact } from 'lodash'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import ScrollableList from 'modules/core/components/ScrollableList'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { events } from 'modules/reports/lib/events'
import { ReportShares } from 'modules/reports/store/collections/reports_shares'
import type Report from 'modules/reports/store/models/Report'
import {
  ReportShareVisibilities,
  type ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'
import { GetAccessesForShareModalDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import AddPeopleCollapsableCardTrigger from './AddPeopleCollapsableCardTrigger'
import Owner, { type OwnerData } from './Owner'
import Sharee, { type ShareeData } from './Sharee'
import { ReportShareInfo, useReportSharesEditor } from './useReportSharesEditor'

interface Props {
  report: Report
  onClose: () => void
}

function ShareReportModal({ report, onClose }: Props): JSX.Element {
  const { access } = useCurrentSession()
  const editor = useReportSharesEditor(report, access.id)
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([])
  const [calloutVisible, setCalloutVisible] = React.useState(true)
  const [addPeopleOpened, setAddPeopleOpened] = React.useState(false)
  const [savedShare, toggleSavedShare] = useToggle(false)
  const [sharesLoaded, setSharesLoaded] = React.useState(false)

  // Collect all access IDs we need to fetch (owner + sharees)
  const accessIdsToFetch = React.useMemo(() => {
    const ids = new Set<string>()
    ids.add(report.authorId)
    editor.currentShares.forEach((share) => ids.add(share.granteeId))
    return Array.from(ids)
  }, [report.authorId, editor.currentShares])

  // Fetch accesses via GraphQL
  const { data: accessesData, loading: loadingAccesses } = useQuery(
    GetAccessesForShareModalDocument,
    {
      variables: { ids: accessIdsToFetch },
      skip: !sharesLoaded || accessIdsToFetch.length === 0,
    }
  )

  // Load report shares on mount
  React.useEffect(() => {
    const fetchReportShares = async () => {
      const reportShares = await ReportShares.fetchForReport(report.id)
      editor.reset(reportShares)
      setSharesLoaded(true)
    }
    fetchReportShares()
  }, [report.id])

  // Create a map of access ID to access data for quick lookup
  const accessesMap = React.useMemo(() => {
    const map = new Map<string, { id: string; avatar?: string | null; fullName: string }>()
    accessesData?.apiCore?.accesses?.forEach((access) => {
      if (access) {
        map.set(access.id, {
          id: access.id,
          avatar: access.avatar,
          fullName: access.fullName,
        })
      }
    })
    return map
  }, [accessesData])

  const owner: OwnerData | null = accessesMap.get(report.authorId) ?? null

  const loading = !sharesLoaded || loadingAccesses
  const shouldDisplayContent = !loading && !savedShare


  const toggleAddPeopleOwned = () => {
    track(events.clickReportShareAddNewPeople)
    setAddPeopleOpened(!addPeopleOpened)
  }

  const handleAddUsers = () => {
    track(events.ReportShareEmployeesSelected)
    const shares = selectedUserIds.map((id) => ({
      granteeId: id,
      visibility: ReportShareVisibilities.Viewer,
    }))

    editor.addShares(shares)

    setAddPeopleOpened(false)
    setSelectedUserIds([])
  }

  const handleCancel = () => onClose()

  const handleSave = async () => {
    track(events.submitReportShare)
    await editor.saveChanges()
    toggleSavedShare(true)()
    onClose()
  }

  const excludeIds = [report.authorId, ...editor.currentShares.map((s) => s.granteeId)]

  const content = () => {
    return (
      <>
        <Box gap="s24">
          {calloutVisible && (
            <Callout
              description={i18n.t('custom_reports.share.modal.callout.description')}
              hideIcon
              type="info"
              onRemove={() => {
                setCalloutVisible(false)
              }}
            />
          )}
          <Box flexGrow width="full">
            <CollapsableCard
              open={addPeopleOpened}
              Card={
                <AddPeopleCollapsableCardTrigger onClick={toggleAddPeopleOwned} icon="add">
                  {i18n.t('custom_reports.share.modal.add_people.button')}
                </AddPeopleCollapsableCardTrigger>
              }
              Content={
                <Box gap="s24">
                  <EmployeeSelectorV2
                    multiple
                    useAccessId
                    value={selectedUserIds}
                    onChange={(state) => {
                      if (state.type === 'manual') {
                        setSelectedUserIds(state.ids)
                      }
                    }}
                    forcedFilters={{ excludeIds, accessStatus: ['active'] }}
                    options={{ disableSelectAll: true }}
                  />
                  <Button onClick={handleAddUsers} disabled={selectedUserIds.length < 1}>
                    {selectedUserIds.length
                      ? i18n.tp('custom_reports.share.modal.add_people.popover_button.selection', {
                          count: selectedUserIds.length,
                        })
                      : i18n.t('custom_reports.share.modal.add_people.popover_button.no_selection')}
                  </Button>
                </Box>
              }
            />
          </Box>
          <Box gap="s16">
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {i18n.t('custom_reports.share.modal.list_title')} ({editor.currentShares.length + 1})
            </Text.Normal>

            <ScrollableList size="medium">
              <Box paddingRight="s8" gap="s16">
                {owner && <Owner owner={owner} />}
                <Sharees
                  shares={editor.currentShares}
                  accessesMap={accessesMap}
                  onVisibilityChange={editor.changeVisibility}
                  onDelete={editor.removeShare}
                />
              </Box>
            </ScrollableList>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <Modal
      title={renderModalTitle(report.title)}
      description={renderModalDescription()}
      illustration="document-edit"
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <Box flexDirection="column" hideScrollBar={false}>
        <Box minHeight="s80">
          {loading && (
            <Box alignItems="center" paddingY="s16">
              <Spinner />
            </Box>
          )}
          {shouldDisplayContent && content()}
        </Box>
      </Box>
    </Modal>
  )
}

function renderModalTitle(reportTitle: string) {
  return i18n.t('custom_reports.share.modal.title', { reportTitle: reportTitle })
}

function renderModalDescription() {
  return i18n.t('custom_reports.share.modal.description_simplified')
}

interface ShareesProps {
  shares: ReportShareInfo[]
  accessesMap: Map<string, ShareeData>
  onVisibilityChange: (shareeId: string, visibility: ReportShareVisibility) => void
  onDelete: (shareeId: string) => void
}

function Sharees({ shares, accessesMap, onVisibilityChange, onDelete }: ShareesProps): JSX.Element {
  const sharees = compact(
    shares.map((share) => {
      const access = accessesMap.get(share.granteeId)

      if (!access) {
        return
      }

      return {
        share: share,
        sharee: access,
      }
    })
  )

  return (
    <Box gap="s16">
      {sharees.map((s) => (
        <Sharee
          key={s.share.granteeId}
          sharee={s.sharee}
          mode={s.share.mode}
          visibility={s.share.visibility}
          onVisibilityChange={(visibility) => onVisibilityChange(s.share.granteeId, visibility)}
          onDelete={() => onDelete(s.share.granteeId)}
        />
      ))}
    </Box>
  )
}

export default ShareReportModal
